import React, { Fragment, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Settings, User, Lock, LogOut as LogOutIcon, ChevronDown } from "react-feather";
import {
  CENTER_NAME,
  clearItem,
  clearLocalItem,
  eventGenerator,
  getItem,
  getLocalItem,
  isUserProfile,
  logOutMessage,
  LOG_OUT,
  profileEvent,
  profileEventMessage,
  setItem,
  setLocalItem,
  Undefined,
  checkStatusCodeSuccess,
} from "../../Constant/common";
import { ChangePassword, GlobalConfig, LogOut, Profile, WelcomeTo } from "../../Constant";
import profileImage from "../../assets/images/avtar/sample.jpg";
import { toast } from "react-toastify";
import { hrStaffImageApi } from "../../Api/HRStaffApi";
import { BaseStaffURL } from "../../Api/Service";
import ThemeToggle from "../../BaseComponents/ThemeToggle";

const HeaderContain = ({ isTablet, isSmallMobile }) => {
  const [open, setOpen] = useState(false);
  const [staffImage, setStaffImage] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const userRole = getLocalItem("role");
  const permissions = JSON.parse(getLocalItem("permissions")) || [];
  const isAdmin =
    userRole === "SuperAdmin" || userRole === "Admin" || userRole?.startsWith("Admin");
  const hasConfigPermission = permissions.some(
    (p) => p.module_name === "Global Configuration" && p.read === 1,
  );
  const showConfig = isAdmin || hasConfigPermission;
  const centerName = getItem(CENTER_NAME) ?? getLocalItem(CENTER_NAME);
  const staffId = getLocalItem("id");
  const userName = getLocalItem("name") || getLocalItem("email") || "User";

  useEffect(() => {
    if (!staffId) return;
    hrStaffImageApi(staffId)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setStaffImage(resp?.data?.staff_image);
        }
      })
      .catch((err) => err);
  }, [staffId]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggleDropdown = () => {
    if (!open && buttonRef.current) {
      // Calculate position immediately before opening
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen((o) => !o);
  };

  const Logout = () => {
    clearLocalItem();
    clearItem();
    toast.success(logOutMessage);
    setLocalItem(LOG_OUT, Date.now());
    navigate("/login");
  };

  const navigateToProfile = () => {
    setItem(isUserProfile, JSON.stringify(true));
    eventGenerator(profileEvent, profileEventMessage);
  };

  const menuItems = [
    ...(showConfig ? [{ label: GlobalConfig, to: "/globalConfiguration", Icon: Settings }] : []),
    { label: Profile, to: "/profile", Icon: User, onClick: navigateToProfile },
    { label: ChangePassword, to: "/changePassword", Icon: Lock },
    { label: LogOut, to: "/login", Icon: LogOutIcon, onClick: Logout, danger: true },
  ];

  return (
    <Fragment>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: isSmallMobile ? 4 : isTablet ? 6 : 10,
        }}
      >
        {/* Center name pill - Hide on tablets and below */}
        {!isTablet && centerName && centerName !== Undefined && (
          <div
            style={{
              padding: "5px 12px",
              borderRadius: 999,
              background: "rgba(99,102,241,0.1)",
              border: "1px solid rgba(99,102,241,0.2)",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--brand-indigo)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {WelcomeTo} {centerName}
          </div>
        )}

        {/* Theme toggle - Show on all devices */}
        <ThemeToggle />

        {/* Profile dropdown */}
        <div style={{ position: "relative" }}>
          <button
            ref={buttonRef}
            onClick={handleToggleDropdown}
            aria-label="User menu"
            aria-expanded={open}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: isSmallMobile ? 4 : 8,
              padding: isSmallMobile ? "2px 4px" : "4px 8px",
              borderRadius: 12,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--nav-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
            }}
          >
            <img
              src={staffImage ? `${BaseStaffURL}${staffImage}` : profileImage}
              alt="Profile"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = profileImage;
              }}
              style={{
                width: isSmallMobile ? 28 : 32,
                height: isSmallMobile ? 28 : 32,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid rgba(99,102,241,0.35)",
              }}
            />
            {/* Username - Hide on tablets and below */}
            {!isTablet && (
              <span
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  maxWidth: 110,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </span>
            )}
            {/* Chevron - Hide on small mobile */}
            {!isSmallMobile && (
              <ChevronDown
                size={13}
                style={{
                  color: "var(--text-muted)",
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            )}
          </button>

          {/* Dropdown rendered via portal */}
          {open &&
            ReactDOM.createPortal(
              <div
                ref={dropdownRef}
                style={{
                  position: "fixed",
                  top: `${dropdownPosition.top}px`,
                  right: `${dropdownPosition.right}px`,
                  background: "var(--dropdown-bg)",
                  border: "1px solid var(--dropdown-border)",
                  borderRadius: 16,
                  boxShadow: "0 16px 48px rgba(0,0,0,0.3)",
                  backdropFilter: "blur(20px)",
                  minWidth: isSmallMobile ? 180 : 200,
                  padding: 6,
                  zIndex: 99999,
                  opacity: 1,
                  transform: "scale(1)",
                  transition: "opacity 0.12s ease, transform 0.12s ease",
                }}
              >
                {/* Show center name in dropdown on mobile if hidden in header */}
                {isTablet && centerName && centerName !== Undefined && (
                  <div
                    style={{
                      padding: "8px 14px",
                      borderBottom: "1px solid var(--glass-border)",
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.6875rem",
                        color: "var(--text-muted)",
                        marginBottom: 2,
                      }}
                    >
                      Current Center
                    </div>
                    <div
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: "var(--brand-indigo)",
                      }}
                    >
                      {centerName}
                    </div>
                  </div>
                )}

                {menuItems.map(({ label, to, Icon, onClick, danger }) => (
                  <Link
                    key={label}
                    to={to}
                    onClick={() => {
                      setOpen(false);
                      onClick?.();
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "9px 14px",
                      borderRadius: 10,
                      fontSize: isSmallMobile ? "0.75rem" : "0.8125rem",
                      fontWeight: 500,
                      color: danger ? "var(--danger)" : "var(--text-primary)",
                      transition: "background 0.15s",
                      textDecoration: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = danger
                        ? "rgba(244,63,94,0.08)"
                        : "var(--nav-hover)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <Icon size={15} strokeWidth={2} />
                    {label}
                  </Link>
                ))}
              </div>,
              document.body,
            )}
        </div>
      </div>
    </Fragment>
  );
};

export default HeaderContain;
